import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Card,
  CardContent,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  LinearProgress,
  ListSubheader
} from '@mui/material';
import {
  Search as SearchIcon,
  Event as EventIcon,
  Route as TripIcon,
  DownloadForOffline as DownloadIcon,
  Email as EmailIcon,
  Filter as FilterIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { useOrganization } from '../../contexts/OrganizationContext';
import { collection, query, where, getDocs, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

// Type for event ticket data
interface Attendee {
  id: string;
  name: string;
  email: string;
  phone: string;
  eventId: string;
  eventTitle: string;
  tripId?: string;
  tripTitle?: string;
  quantity: number;
  totalPrice: number;
  purchaseDate: Date;
  status: string;
  paymentStatus: string;
  checkInDate?: Date;
  source: 'event' | 'trip';
}

// Type for statistics
interface AttendeeStats {
  totalAttendees: number;
  totalRevenue: number;
  totalEvents: number;
  totalTrips: number;
  checkedInCount: number;
  pendingPaymentsCount: number;
}

const OrganizationAttendees: React.FC = () => {
  const { id, eventId } = useParams<{ id: string; eventId?: string }>();
  const { activeOrganization } = useOrganization();

  // Component state
  const [tabValue, setTabValue] = useState(0);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [filteredAttendees, setFilteredAttendees] = useState<Attendee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [sortField, setSortField] = useState<keyof Attendee>('purchaseDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [eventDetails, setEventDetails] = useState<{
    name: string;
    date?: Date;
    description?: string;
    location?: string;
  } | null>(null);
  const [systemEvents, setSystemEvents] = useState<{id: string, name: string}[]>([]);
  const [systemTrips, setSystemTrips] = useState<{id: string, name: string}[]>([]);
  const [selectedEventOrTripId, setSelectedEventOrTripId] = useState<string>('all');

  // Statistics state
  const [stats, setStats] = useState<AttendeeStats>({
    totalAttendees: 0,
    totalRevenue: 0,
    totalEvents: 0,
    totalTrips: 0,
    checkedInCount: 0,
    pendingPaymentsCount: 0
  });

  useEffect(() => {
    if (id) {
      fetchAttendees();
      
      // If eventId is provided, fetch event details
      if (eventId) {
        fetchEventDetails();
      }
      
      // Fetch events and trips for the filter
      fetchEventsAndTrips();
    }
  }, [id, eventId]);

  useEffect(() => {
    // Apply filters when search term or other filters change
    let filtered = [...attendees];
    
    // Apply search filter
    if (searchTerm.trim()) {
      const termLower = searchTerm.toLowerCase();
      filtered = filtered.filter(attendee => 
        attendee.name.toLowerCase().includes(termLower) || 
        attendee.email.toLowerCase().includes(termLower) ||
        (attendee.eventTitle && attendee.eventTitle.toLowerCase().includes(termLower)) ||
        (attendee.tripTitle && attendee.tripTitle.toLowerCase().includes(termLower))
      );
    }
    
    // Apply tab filters
    if (tabValue === 1) { // Events only
      filtered = filtered.filter(a => a.source === 'event');
    } else if (tabValue === 2) { // Trips only
      filtered = filtered.filter(a => a.source === 'trip');
    }
    
    // Apply source filter
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(a => a.source === sourceFilter);
    }
    
    // Apply payment status filter
    if (paymentStatusFilter !== 'all') {
      filtered = filtered.filter(a => a.paymentStatus === paymentStatusFilter);
    }
    
    // Apply specific event/trip filter
    if (selectedEventOrTripId !== 'all') {
      filtered = filtered.filter(a => 
        a.eventId === selectedEventOrTripId || 
        a.tripId === selectedEventOrTripId
      );
    }
    
    // Apply event ID filter if provided
    if (eventId) {
      filtered = filtered.filter(a => a.eventId === eventId || a.tripId === eventId);
    }
    
    setFilteredAttendees(filtered);
    
    // Update statistics based on the current filter set
    updateStats(filtered);
  }, [attendees, searchTerm, tabValue, sourceFilter, paymentStatusFilter, selectedEventOrTripId, eventId]);

  const fetchAttendees = async () => {
    try {
       setLoading(true);
       setError(null);

       // Fetch event tickets - try multiple possible collection paths
       const eventAttendees: Attendee[] = [];
       const tripParticipants: Attendee[] = [];

       // Method 1: Check eventTickets collection
       try {
          const eventTicketsQuery = query(
             collection(db, 'eventTickets'),
             where('organizationId', '==', id)
          );
          const eventTicketsSnapshot = await getDocs(eventTicketsQuery);

          eventTicketsSnapshot.docs.forEach((doc) => {
             const data = doc.data();
             eventAttendees.push({
                id: doc.id,
                name: data.name || data.displayName || 'Unknown',
                email: data.email || 'N/A',
                phone: data.phone || 'N/A',
                eventId: data.eventId || '',
                eventTitle: data.eventName || data.eventTitle || 'Unknown Event',
                tripId: '',
                tripTitle: '',
                quantity: data.quantity || 1,
                totalPrice: data.price || 0,
                purchaseDate: data.purchaseDate?.toDate() || new Date(),
                status: data.status || 'registered',
                paymentStatus: data.paymentStatus || 'paid',
                source: 'event' as const,
                checkInDate: data.checkInDate?.toDate(),
             });
          });
       } catch (error) {
          console.error('Error fetching from eventTickets:', error);
       }

       // Also try with orgId as alternative field name
       if (eventAttendees.length === 0) {
          try {
             const eventTicketsQuery = query(
                collection(db, 'eventTickets'),
                where('orgId', '==', id)
             );
             const eventTicketsSnapshot = await getDocs(eventTicketsQuery);

             eventTicketsSnapshot.docs.forEach((doc) => {
                const data = doc.data();
                eventAttendees.push({
                   id: doc.id,
                   name: data.name || data.displayName || 'Unknown',
                   email: data.email || 'N/A',
                   phone: data.phone || 'N/A',
                   eventId: data.eventId || '',
                   eventTitle: data.eventName || data.eventTitle || 'Unknown Event',
                   tripId: '',
                   tripTitle: '',
                   quantity: data.quantity || 1,
                   totalPrice: data.price || 0,
                   purchaseDate: data.purchaseDate?.toDate() || new Date(),
                   status: data.status || 'registered',
                   paymentStatus: data.paymentStatus || 'paid',
                   source: 'event' as const,
                   checkInDate: data.checkInDate?.toDate(),
                });
             });
          } catch (error) {
             console.error('Error fetching from eventTickets with orgId:', error);
          }
       }

       // Look for manually created event attendees in a dedicated collection
       try {
          // Try an eventattendees collection (all lowercase)
          const eventAttendeesQuery = query(
             collection(db, 'eventattendees'),
             where('organizationId', '==', id)
          );
          const eventAttendeesSnapshot = await getDocs(eventAttendeesQuery);

          eventAttendeesSnapshot.docs.forEach((doc) => {
             const data = doc.data();
             eventAttendees.push({
                id: doc.id,
                name: data.name || data.displayName || 'Unknown',
                email: data.email || 'N/A',
                phone: data.phone || 'N/A',
                eventId: data.eventId || '',
                eventTitle: data.eventName || data.eventTitle || 'Unknown Event',
                tripId: '',
                tripTitle: '',
                quantity: data.quantity || 1,
                totalPrice: data.price || 0,
                purchaseDate: data.purchaseDate?.toDate() || new Date(),
                status: data.status || 'registered',
                paymentStatus: data.paymentStatus || 'paid',
                source: 'event' as const,
                checkInDate: data.checkInDate?.toDate(),
             });
          });
       } catch (error) {
          console.error('Error fetching from eventattendees:', error);
       }

       // Try with capitalized first letter
       try {
          const eventAttendeesQuery = query(
             collection(db, 'eventAttendees'),
             where('organizationId', '==', id)
          );
          const eventAttendeesSnapshot = await getDocs(eventAttendeesQuery);

          eventAttendeesSnapshot.docs.forEach((doc) => {
             const data = doc.data();
             eventAttendees.push({
                id: doc.id,
                name: data.name || data.displayName || 'Unknown',
                email: data.email || 'N/A',
                phone: data.phone || 'N/A',
                eventId: data.eventId || '',
                eventTitle: data.eventName || data.eventTitle || 'Unknown Event',
                tripId: '',
                tripTitle: '',
                quantity: data.quantity || 1,
                totalPrice: data.price || 0,
                purchaseDate: data.purchaseDate?.toDate() || new Date(),
                status: data.status || 'registered',
                paymentStatus: data.paymentStatus || 'paid',
                source: 'event' as const,
                checkInDate: data.checkInDate?.toDate(),
             });
          });
       } catch (error) {
          console.error('Error fetching from eventAttendees:', error);
       }

       // Also try with orgId as alternative field name
       try {
          const eventAttendeesQuery = query(
             collection(db, 'eventAttendees'),
             where('orgId', '==', id)
          );
          const eventAttendeesSnapshot = await getDocs(eventAttendeesQuery);

          eventAttendeesSnapshot.docs.forEach((doc) => {
             const data = doc.data();
             eventAttendees.push({
                id: doc.id,
                name: data.name || data.displayName || 'Unknown',
                email: data.email || 'N/A',
                phone: data.phone || 'N/A',
                eventId: data.eventId || '',
                eventTitle: data.eventName || data.eventTitle || 'Unknown Event',
                tripId: '',
                tripTitle: '',
                quantity: data.quantity || 1,
                totalPrice: data.price || 0,
                purchaseDate: data.purchaseDate?.toDate() || new Date(),
                status: data.status || 'registered',
                paymentStatus: data.paymentStatus || 'paid',
                source: 'event' as const,
                checkInDate: data.checkInDate?.toDate(),
             });
          });
       } catch (error) {
          console.error('Error fetching from eventAttendees with orgId:', error);
       }

       // Method 2: Check tripParticipants collection
       try {
          const tripParticipantsQuery = query(
             collection(db, 'tripParticipants'),
             where('organizationId', '==', id)
          );
          const tripParticipantsSnapshot = await getDocs(tripParticipantsQuery);

          tripParticipantsSnapshot.docs.forEach((doc) => {
             const data = doc.data();
             tripParticipants.push({
                id: doc.id,
                name: data.name || data.displayName || 'Unknown',
                email: data.email || 'N/A',
                phone: data.phone || 'N/A',
                tripId: data.tripId || '',
                tripTitle: data.tripName || data.tripTitle || 'Unknown Trip',
                eventId: '',
                eventTitle: '',
                quantity: data.quantity || 1,
                totalPrice: data.price || 0,
                purchaseDate: data.purchaseDate?.toDate() || new Date(),
                status: data.status || 'registered',
                paymentStatus: data.paymentStatus || 'paid',
                source: 'trip' as const,
                checkInDate: data.checkInDate?.toDate(),
             });
          });
       } catch (error) {
          console.error('Error fetching from tripParticipants:', error);
       }

       // Also try with orgId as alternative field name
       if (tripParticipants.length === 0) {
          try {
             const tripParticipantsQuery = query(
                collection(db, 'tripParticipants'),
                where('orgId', '==', id)
             );
             const tripParticipantsSnapshot = await getDocs(tripParticipantsQuery);

             tripParticipantsSnapshot.docs.forEach((doc) => {
                const data = doc.data();
                tripParticipants.push({
                   id: doc.id,
                   name: data.name || data.displayName || 'Unknown',
                   email: data.email || 'N/A',
                   phone: data.phone || 'N/A',
                   tripId: data.tripId || '',
                   tripTitle: data.tripName || data.tripTitle || 'Unknown Trip',
                   eventId: '',
                   eventTitle: '',
                   quantity: data.quantity || 1,
                   totalPrice: data.price || 0,
                   purchaseDate: data.purchaseDate?.toDate() || new Date(),
                   status: data.status || 'registered',
                   paymentStatus: data.paymentStatus || 'paid',
                   source: 'trip' as const,
                   checkInDate: data.checkInDate?.toDate(),
                });
             });
          } catch (error) {
             console.error('Error fetching from tripParticipants with orgId:', error);
          }
       }

       // Try trip_participants as alternative collection name
       try {
          const tripParticipantsQuery = query(
             collection(db, 'trip_participants'),
             where('organizationId', '==', id)
          );
          const tripParticipantsSnapshot = await getDocs(tripParticipantsQuery);

          tripParticipantsSnapshot.docs.forEach((doc) => {
             const data = doc.data();
             tripParticipants.push({
                id: doc.id,
                name: data.name || data.displayName || 'Unknown',
                email: data.email || 'N/A',
                phone: data.phone || 'N/A',
                tripId: data.tripId || '',
                tripTitle: data.tripName || data.tripTitle || 'Unknown Trip',
                eventId: '',
                eventTitle: '',
                quantity: data.quantity || 1,
                totalPrice: data.price || 0,
                purchaseDate: data.purchaseDate?.toDate() || new Date(),
                status: data.status || 'registered',
                paymentStatus: data.paymentStatus || 'paid',
                source: 'trip' as const,
                checkInDate: data.checkInDate?.toDate(),
             });
          });
       } catch (error) {
          console.error('Error fetching from trip_participants:', error);
       }

       // Method 3: Try direct lookup of specific events and trips known to have participants
       if (eventAttendees.length === 0 && tripParticipants.length === 0) {
          try {
             // Get all events for this organization
             const eventsQuery = query(collection(db, 'events'), where('organizationId', '==', id));
             const eventsSnapshot = await getDocs(eventsQuery);

             // Check events one by one
             for (const eventDoc of eventsSnapshot.docs) {
                const eventData = eventDoc.data();

                await fetchEventAttendeesForCollection(
                   eventDoc.id,
                   eventData.name || eventData.title || 'Unnamed Event',
                   eventAttendees
                );
             }
          } catch (error) {
             console.error('Error during event scan:', error);
          }
       }

       // Always check specific trips, regardless of whether we found attendees elsewhere

       await checkSpecificTrips(tripParticipants);

       // Also check specific events

       await checkSpecificEvents(eventAttendees);

       if (eventAttendees.length === 0 && tripParticipants.length === 0) {
       }

       // Combine event attendees and trip participants
       const allAttendees = [...eventAttendees, ...tripParticipants];

       setAttendees(allAttendees);
       setFilteredAttendees(allAttendees);
       updateStats(allAttendees);

       // If we're looking at a specific event, filter only for that
       if (eventId) {
          const filteredForEvent = allAttendees.filter(
             (a) => a.eventId === eventId || a.tripId === eventId
          );
          setFilteredAttendees(filteredForEvent);
          updateStats(filteredForEvent);
       }

       setLoading(false);
    } catch (error) {
      console.error('Error fetching attendees:', error);
      setError('Failed to load attendees');
      setLoading(false);
    }
  };
  
  // Helper function to fetch attendees for a specific event
  const fetchEventAttendeesForCollection = async (eventId: string, eventTitle: string, attendeesList: Attendee[]) => {
    try {
       // Try to get attendees from subcollection
       const attendeesQuery = collection(db, `events/${eventId}/attendees`);
       const attendeesSnapshot = await getDocs(attendeesQuery);

       if (!attendeesSnapshot.empty) {
          attendeesSnapshot.docs.forEach((attendeeDoc) => {
             const data = attendeeDoc.data();
             attendeesList.push({
                id: attendeeDoc.id,
                name: data.name || data.displayName || 'Unknown',
                email: data.email || 'N/A',
                phone: data.phone || 'N/A',
                eventId: eventId,
                eventTitle: eventTitle,
                tripId: '',
                tripTitle: '',
                quantity: data.quantity || 1,
                totalPrice: data.price || 0,
                purchaseDate: data.purchaseDate?.toDate() || new Date(),
                status: data.status || 'registered',
                paymentStatus: data.paymentStatus || 'paid',
                source: 'event' as const,
                checkInDate: data.checkInDate?.toDate(),
             });
          });
       }

       // Also try tickets subcollection as alternative
       const ticketsQuery = collection(db, `events/${eventId}/tickets`);
       const ticketsSnapshot = await getDocs(ticketsQuery);

       if (!ticketsSnapshot.empty) {
          ticketsSnapshot.docs.forEach((ticketDoc) => {
             const data = ticketDoc.data();
             attendeesList.push({
                id: ticketDoc.id,
                name: data.name || data.displayName || 'Unknown',
                email: data.email || 'N/A',
                phone: data.phone || 'N/A',
                eventId: eventId,
                eventTitle: eventTitle,
                tripId: '',
                tripTitle: '',
                quantity: data.quantity || 1,
                totalPrice: data.price || 0,
                purchaseDate: data.purchaseDate?.toDate() || new Date(),
                status: data.status || 'registered',
                paymentStatus: data.paymentStatus || 'paid',
                source: 'event' as const,
                checkInDate: data.checkInDate?.toDate(),
             });
          });
       }

       // Try registrations subcollection as another alternative
       const registrationsQuery = collection(db, `events/${eventId}/registrations`);
       const registrationsSnapshot = await getDocs(registrationsQuery);

       if (!registrationsSnapshot.empty) {
          registrationsSnapshot.docs.forEach((regDoc) => {
             const data = regDoc.data();
             attendeesList.push({
                id: regDoc.id,
                name: data.name || data.displayName || 'Unknown',
                email: data.email || 'N/A',
                phone: data.phone || 'N/A',
                eventId: eventId,
                eventTitle: eventTitle,
                tripId: '',
                tripTitle: '',
                quantity: data.quantity || 1,
                totalPrice: data.price || data.amount || 0,
                purchaseDate:
                   data.registrationDate?.toDate() || data.createdAt?.toDate() || new Date(),
                status: data.status || 'registered',
                paymentStatus: data.paymentStatus || 'paid',
                source: 'event' as const,
                checkInDate: data.checkInDate?.toDate(),
             });
          });
       }
    } catch (error) {}
  };

  // Helper function to check specific trips we know should have participants
  const checkSpecificTrips = async (tripParticipantsList: Attendee[]) => {
    // Check for specific trips that should have participants
    const specificTripIds = ['tXO1D1wjEcTsOjL7ZiqG', 'zm4tkLMkE23UByERFBxv'];
    
    for (const tripId of specificTripIds) {
       try {
          const tripDoc = await getDoc(doc(db, 'trips', tripId));

          if (tripDoc.exists()) {
             const data = tripDoc.data();

             // Check if trip belongs to the current organization
             // Note: Some trips might not have an explicit organizationId
             if (data.organizationId && data.organizationId !== id) {
                continue;
             }

             // Check if the trip document has an embedded participants array
             if (
                data.participants &&
                Array.isArray(data.participants) &&
                data.participants.length > 0
             ) {
                // Add each participant from the embedded array to our participants list
                data.participants.forEach((participant: any) => {
                   // Generate a stable ID for the participant
                   const participantId =
                      participant.userId ||
                      participant.id ||
                      `${tripId}-${participant.email ? participant.email.replace(/[^a-zA-Z0-9]/g, '') : Math.random().toString(36).substring(2, 10)}`;

                   tripParticipantsList.push({
                      id: participantId,
                      name: participant.displayName || participant.name || 'Unknown',
                      email: participant.email || 'N/A',
                      phone: participant.phone || 'N/A',
                      tripId: tripId,
                      tripTitle: data.title || data.name || data.tripName || 'Unnamed Trip',
                      eventId: '',
                      eventTitle: '',
                      quantity: 1,
                      totalPrice: participant.price || participant.amount || 0,
                      purchaseDate:
                         participant.joinedDate?.toDate() ||
                         participant.acceptedAt?.toDate() ||
                         participant.createdAt?.toDate() ||
                         data.createdAt?.toDate() ||
                         new Date(),
                      status: participant.status || 'joined',
                      paymentStatus: participant.paymentStatus || 'free',
                      source: 'trip' as const,
                      checkInDate: participant.checkInDate?.toDate(),
                   });
                });
             } else {
             }

             // Also check the subcollections (existing code will handle this)
             await fetchTripParticipantsForCollection(
                tripId,
                data.title || data.name || data.tripName || 'Unnamed Trip',
                tripParticipantsList
             );
          } else {
          }
       } catch (error) {
          console.error(`Error checking specific trip ${tripId}:`, error);
       }
    }
    

  };

  // Helper function to fetch participants for a specific trip
  const fetchTripParticipantsForCollection = async (
     tripId: string,
     tripTitle: string,
     participantsList: Attendee[]
  ) => {
     try {
        // Try to get participants from subcollection
        const participantsQuery = collection(db, `trips/${tripId}/participants`);
        const participantsSnapshot = await getDocs(participantsQuery);

        if (!participantsSnapshot.empty) {
           participantsSnapshot.docs.forEach((participantDoc) => {
              const data = participantDoc.data();
              participantsList.push({
                 id: participantDoc.id,
                 name: data.name || data.displayName || 'Unknown',
                 email: data.email || 'N/A',
                 phone: data.phone || 'N/A',
                 tripId: tripId,
                 tripTitle: tripTitle,
                 eventId: '',
                 eventTitle: '',
                 quantity: 1,
                 totalPrice: data.price || data.amount || 0,
                 purchaseDate: data.joinedDate?.toDate() || data.createdAt?.toDate() || new Date(),
                 status: data.status || 'joined',
                 paymentStatus: data.paymentStatus || 'free',
                 source: 'trip' as const,
                 checkInDate: data.checkInDate?.toDate(),
              });
           });
        }

        // Also try members subcollection as alternative
        const membersQuery = collection(db, `trips/${tripId}/members`);
        const membersSnapshot = await getDocs(membersQuery);

        if (!membersSnapshot.empty) {
           membersSnapshot.docs.forEach((memberDoc) => {
              const data = memberDoc.data();
              participantsList.push({
                 id: memberDoc.id,
                 name: data.name || data.displayName || 'Unknown',
                 email: data.email || 'N/A',
                 phone: data.phone || 'N/A',
                 tripId: tripId,
                 tripTitle: tripTitle,
                 eventId: '',
                 eventTitle: '',
                 quantity: 1,
                 totalPrice: data.price || data.amount || 0,
                 purchaseDate: data.joinedDate?.toDate() || data.createdAt?.toDate() || new Date(),
                 status: data.status || 'joined',
                 paymentStatus: data.paymentStatus || 'free',
                 source: 'trip' as const,
                 checkInDate: data.checkInDate?.toDate(),
              });
           });
        }
     } catch (error) {}
  };

  // Helper function to check specific events we know should have attendees
  const checkSpecificEvents = async (attendeesList: Attendee[]) => {
     // Check for specific events that should have attendees
     // Add event IDs here as needed
     const specificEventIds = [
        '4rHpuMgjJUH1vCBe4pIj',
        '5r1XOqxdn1TP151NuV35',
        'Rd2oEkj0R1JYdJauSQM1',
     ];

     // Also check the Gala Events in Madrid trip as an event (it might be both a trip and an event)
     // This is a special case where a trip might also have event attendees
     specificEventIds.push('tXO1D1wjEcTsOjL7ZiqG');

     for (const eventId of specificEventIds) {
        try {
           const eventDoc = await getDoc(doc(db, 'events', eventId));

           if (eventDoc.exists()) {
              const data = eventDoc.data();

              // Check if event belongs to the current organization
              if (data.organizationId && data.organizationId !== id) {
                 continue;
              }

              // Check if the event document has an embedded attendees array
              if (data.attendees && Array.isArray(data.attendees) && data.attendees.length > 0) {
                 // Add each attendee from the embedded array to our attendees list
                 data.attendees.forEach((attendee: any) => {
                    // Generate a stable ID for the attendee
                    const attendeeId =
                       attendee.userId ||
                       attendee.id ||
                       `${eventId}-${attendee.email ? attendee.email.replace(/[^a-zA-Z0-9]/g, '') : Math.random().toString(36).substring(2, 10)}`;

                    attendeesList.push({
                       id: attendeeId,
                       name: attendee.displayName || attendee.name || 'Unknown',
                       email: attendee.email || 'N/A',
                       phone: attendee.phone || 'N/A',
                       eventId: eventId,
                       eventTitle: data.title || data.name || data.eventName || 'Unnamed Event',
                       tripId: '',
                       tripTitle: '',
                       quantity: attendee.quantity || 1,
                       totalPrice: attendee.price || attendee.amount || 0,
                       purchaseDate:
                          attendee.purchaseDate?.toDate() ||
                          attendee.createdAt?.toDate() ||
                          data.createdAt?.toDate() ||
                          new Date(),
                       status: attendee.status || 'registered',
                       paymentStatus: attendee.paymentStatus || 'paid',
                       source: 'event' as const,
                       checkInDate: attendee.checkInDate?.toDate(),
                    });
                 });
              } else {
              }

              // Also check for a 'registrants' array which might be used instead of 'attendees'
              if (
                 data.registrants &&
                 Array.isArray(data.registrants) &&
                 data.registrants.length > 0
              ) {
                 data.registrants.forEach((registrant: any) => {
                    const registrantId =
                       registrant.userId ||
                       registrant.id ||
                       `${eventId}-${registrant.email ? registrant.email.replace(/[^a-zA-Z0-9]/g, '') : Math.random().toString(36).substring(2, 10)}`;

                    attendeesList.push({
                       id: registrantId,
                       name: registrant.displayName || registrant.name || 'Unknown',
                       email: registrant.email || 'N/A',
                       phone: registrant.phone || 'N/A',
                       eventId: eventId,
                       eventTitle: data.title || data.name || data.eventName || 'Unnamed Event',
                       tripId: '',
                       tripTitle: '',
                       quantity: registrant.quantity || 1,
                       totalPrice: registrant.price || registrant.amount || 0,
                       purchaseDate:
                          registrant.purchaseDate?.toDate() ||
                          registrant.createdAt?.toDate() ||
                          data.createdAt?.toDate() ||
                          new Date(),
                       status: registrant.status || 'registered',
                       paymentStatus: registrant.paymentStatus || 'paid',
                       source: 'event' as const,
                       checkInDate: registrant.checkInDate?.toDate(),
                    });
                 });
              }

              // Also check the subcollections (existing code will handle this)
              await fetchEventAttendeesForCollection(
                 eventId,
                 data.title || data.name || data.eventName || 'Unnamed Event',
                 attendeesList
              );
           } else {
           }
        } catch (error) {
           console.error(`Error checking specific event ${eventId}:`, error);
        }
     }
  };

  // Fetch specific event details if an eventId is provided
  const fetchEventDetails = async () => {
     if (!eventId) return;

     try {
        // First try events collection
        const eventDoc = await getDoc(doc(db, 'events', eventId));

        if (eventDoc.exists()) {
           const data = eventDoc.data();

           setEventDetails({
              name: data.name || data.eventName || data.title || 'Unnamed Event',
              date: data.date?.toDate() || data.eventDate?.toDate() || data.startDate?.toDate(),
              description: data.description || data.eventDescription || '',
              location: data.location || data.venue || '',
           });
        } else {
           // Try trips collection
           const tripDoc = await getDoc(doc(db, 'trips', eventId));
           if (tripDoc.exists()) {
              const data = tripDoc.data();

              setEventDetails({
                 name: data.name || data.tripName || data.title || 'Unnamed Trip',
                 date: data.startDate?.toDate() || data.date?.toDate(),
                 description: data.description || '',
                 location: data.destination || data.location || '',
              });

              // When it's a trip, specifically fetch its participants directly
              const tripParticipants: Attendee[] = [];
              await fetchTripParticipantsForCollection(
                 eventId,
                 data.name || data.tripName || data.title || 'Unnamed Trip',
                 tripParticipants
              );

              if (tripParticipants.length > 0) {
                 setAttendees(tripParticipants);
                 setFilteredAttendees(tripParticipants);
                 updateStats(tripParticipants);
              } else {
              }
           } else {
              console.error('Event/Trip not found');
              setError('Event or trip not found');
           }
        }
     } catch (error) {
        console.error('Error fetching event details:', error);
        setError('Failed to load event details');
     }
  };

  // Fetch available events and trips
  const fetchEventsAndTrips = async () => {
     try {
        // Fetch events
        const eventsQuery = query(collection(db, 'events'), where('organizationId', '==', id));
        const eventsSnapshot = await getDocs(eventsQuery);
        const eventsData = eventsSnapshot.docs.map((doc) => ({
           id: doc.id,
           name: doc.data().name || doc.data().title || doc.data().eventName || 'Unnamed Event',
        }));

        // Fetch trips
        const tripsQuery = query(collection(db, 'trips'), where('organizationId', '==', id));
        const tripsSnapshot = await getDocs(tripsQuery);
        const tripsData = tripsSnapshot.docs.map((doc) => ({
           id: doc.id,
           name: doc.data().name || doc.data().title || doc.data().tripName || 'Unnamed Trip',
        }));

        // Try alternative organizationId field if needed
        if (eventsData.length === 0) {
           const altEventsQuery = query(collection(db, 'events'), where('orgId', '==', id));
           const altEventsSnapshot = await getDocs(altEventsQuery);
           const altEventsData = altEventsSnapshot.docs.map((doc) => ({
              id: doc.id,
              name: doc.data().name || doc.data().title || doc.data().eventName || 'Unnamed Event',
           }));

           eventsData.push(...altEventsData);
        }

        if (tripsData.length === 0) {
           const altTripsQuery = query(collection(db, 'trips'), where('orgId', '==', id));
           const altTripsSnapshot = await getDocs(altTripsQuery);
           const altTripsData = altTripsSnapshot.docs.map((doc) => ({
              id: doc.id,
              name: doc.data().name || doc.data().title || doc.data().tripName || 'Unnamed Trip',
           }));

           tripsData.push(...altTripsData);
        }

        // Add the specific trips we know should be in the system
        const specificTripIds = ['tXO1D1wjEcTsOjL7ZiqG', 'zm4tkLMkE23UByERFBxv'];
        for (const tripId of specificTripIds) {
           if (!tripsData.some((trip) => trip.id === tripId)) {
              try {
                 const tripDoc = await getDoc(doc(db, 'trips', tripId));
                 if (tripDoc.exists()) {
                    const data = tripDoc.data();
                    tripsData.push({
                       id: tripId,
                       name: data.title || data.name || data.tripName || 'Unnamed Trip',
                    });
                 }
              } catch (error) {
                 console.error(`Error checking specific trip ${tripId}:`, error);
              }
           }
        }

        setSystemEvents(eventsData);
        setSystemTrips(tripsData);
     } catch (error) {
        console.error('Error fetching events and trips for filter:', error);
     }
  };

  const updateStats = (attendeesList: Attendee[]) => {
    const totalAttendees = attendeesList.length;
    const totalRevenue = attendeesList.reduce((sum, a) => sum + a.totalPrice, 0);
    const checkedInCount = attendeesList.filter(a => a.checkInDate).length;
    const pendingPaymentsCount = attendeesList.filter(a => a.paymentStatus === 'pending').length;
    
    // Count unique events and trips
    const uniqueEvents = new Set(
      attendeesList.filter(a => a.source === 'event' && a.eventId).map(a => a.eventId)
    );
    const uniqueTrips = new Set(
      attendeesList.filter(a => a.source === 'trip' && a.tripId).map(a => a.tripId)
    );
    
    setStats(prev => ({
      ...prev,
      totalAttendees,
      totalRevenue,
      checkedInCount,
      pendingPaymentsCount,
      totalEvents: uniqueEvents.size,
      totalTrips: uniqueTrips.size
    }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0); // Reset to first page when changing tabs
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleSourceFilterChange = (event: SelectChangeEvent<string>) => {
    setSourceFilter(event.target.value);
    setPage(0);
  };

  const handlePaymentStatusFilterChange = (event: SelectChangeEvent<string>) => {
    setPaymentStatusFilter(event.target.value);
    setPage(0);
  };

  const handleEventOrTripFilterChange = (event: SelectChangeEvent<string>) => {
    setSelectedEventOrTripId(event.target.value);
    setPage(0); // Reset to first page when filtering
  };

  const handleSort = (property: keyof Attendee) => {
    const isAsc = sortField === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortField(property);
    
    // Sort the filtered attendees
    const sortedAttendees = [...filteredAttendees].sort((a, b) => {
      // Handle different types of properties
      if (property === 'purchaseDate' || property === 'checkInDate') {
        const aDate = a[property] ? (a[property] as Date).getTime() : 0;
        const bDate = b[property] ? (b[property] as Date).getTime() : 0;
        return (sortOrder === 'asc' ? aDate - bDate : bDate - aDate);
      } else if (property === 'totalPrice' || property === 'quantity') {
        return sortOrder === 'asc' 
          ? (a[property] as number) - (b[property] as number) 
          : (b[property] as number) - (a[property] as number);
      } else {
        // For string properties
        const aStr = String(a[property] || '').toLowerCase();
        const bStr = String(b[property] || '').toLowerCase();
        return sortOrder === 'asc' 
          ? aStr.localeCompare(bStr) 
          : bStr.localeCompare(aStr);
      }
    });
    
    setFilteredAttendees(sortedAttendees);
  };

  const getStatusChipColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status.toLowerCase()) {
      case 'checked_in':
      case 'checked in':
      case 'attended':
      case 'accepted':
        return 'success';
      case 'pending':
      case 'reserved':
        return 'warning';
      case 'cancelled':
      case 'canceled':
      case 'refunded':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPaymentStatusChipColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (status.toLowerCase()) {
      case 'paid':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'declined':
        return 'error';
      case 'free':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDate = (date?: Date) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Export to CSV function
  const exportToCsv = () => {
    const headers = ['Name', 'Email', 'Phone', 'Source', 'Event/Trip', 'Purchase Date', 'Status', 'Payment Status', 'Total Price'];
    
    const csvData = filteredAttendees.map(a => [
      a.name,
      a.email,
      a.phone,
      a.source === 'event' ? 'Event' : 'Trip',
      a.source === 'event' ? a.eventTitle : a.tripTitle,
      formatDate(a.purchaseDate),
      a.status,
      a.paymentStatus,
      a.totalPrice.toString()
    ]);
    
    // Add headers
    csvData.unshift(headers);
    
    // Convert to CSV string
    const csvString = csvData.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    // Create download link
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${activeOrganization?.name || 'organization'}-attendees.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {eventId && eventDetails ? (
          // Event-specific view
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {eventDetails.name} - Attendees
              </Typography>
              <Button 
                variant="outlined"
                onClick={() => window.history.back()}
              >
                Back to Events
              </Button>
            </Box>
            
            {eventDetails.date && (
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Date: {formatDate(eventDetails.date)}
              </Typography>
            )}
            
            {eventDetails.location && (
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Location: {eventDetails.location}
              </Typography>
            )}
            
            {eventDetails.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {eventDetails.description}
              </Typography>
            )}
          </>
        ) : (
          // Organization-wide attendees view
          <Typography variant="h4" component="h1" gutterBottom>
            {activeOrganization?.name} Attendees
          </Typography>
        )}
        
        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={eventId ? 4 : 3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Attendees
                </Typography>
                <Typography variant="h4">
                  {stats.totalAttendees}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {stats.checkedInCount} checked in ({Math.round((stats.checkedInCount / (stats.totalAttendees || 1)) * 100)}%)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={eventId ? 4 : 3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Revenue
                </Typography>
                <Typography variant="h4">
                  {formatCurrency(stats.totalRevenue)}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {stats.pendingPaymentsCount} pending payments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          {!eventId && (
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Events with Attendees
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalEvents}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(stats.totalAttendees / (stats.totalEvents || 1))} avg. attendees per event
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
          
          {eventId ? (
            <Grid item xs={12} sm={6} md={4}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Check-in Rate
                  </Typography>
                  <Typography variant="h4">
                    {Math.round((stats.checkedInCount / (stats.totalAttendees || 1)) * 100)}%
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ width: '100%', mt: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={(stats.checkedInCount / (stats.totalAttendees || 1)) * 100}
                      color="success"
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Trips with Participants
                  </Typography>
                  <Typography variant="h4">
                    {stats.totalTrips}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {Math.round(filteredAttendees.filter(a => a.source === 'trip').length / (stats.totalTrips || 1))} avg. participants per trip
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
        
        {/* Tabs - Only show when not in event-specific view */}
        {!eventId && (
          <Paper sx={{ mb: 3 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="All Attendees" />
              <Tab label="Event Attendees" icon={<EventIcon fontSize="small" />} iconPosition="start" />
              <Tab label="Trip Participants" icon={<TripIcon fontSize="small" />} iconPosition="start" />
            </Tabs>
          </Paper>
        )}
        
        {/* Search and Filter Controls */}
        <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search attendees..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ flexGrow: 1, maxWidth: 500 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          {/* Only show source filter when not in event-specific view */}
          {!eventId && (
            <FormControl size="small" variant="outlined" sx={{ minWidth: 150 }}>
              <InputLabel id="source-filter-label">Source</InputLabel>
              <Select
                labelId="source-filter-label"
                value={sourceFilter}
                onChange={handleSourceFilterChange}
                label="Source"
              >
                <MenuItem value="all">All Sources</MenuItem>
                <MenuItem value="event">Events</MenuItem>
                <MenuItem value="trip">Trips</MenuItem>
              </Select>
            </FormControl>
          )}
          
          {/* Events and Trips filter */}
          {!eventId && (
            <FormControl size="small" variant="outlined" sx={{ minWidth: 200 }}>
              <InputLabel id="event-trip-filter-label">Event/Trip</InputLabel>
              <Select
                labelId="event-trip-filter-label"
                value={selectedEventOrTripId}
                onChange={handleEventOrTripFilterChange}
                label="Event/Trip"
              >
                <MenuItem value="all">All Events & Trips</MenuItem>
                {systemEvents.length > 0 && (
                  <ListSubheader>Events</ListSubheader>
                )}
                {systemEvents.map(event => (
                  <MenuItem key={`event-${event.id}`} value={event.id}>
                    {event.name}
                  </MenuItem>
                ))}
                {systemTrips.length > 0 && (
                  <ListSubheader>Trips</ListSubheader>
                )}
                {systemTrips.map(trip => (
                  <MenuItem key={`trip-${trip.id}`} value={trip.id}>
                    {trip.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          <FormControl size="small" variant="outlined" sx={{ minWidth: 150 }}>
            <InputLabel id="payment-status-filter-label">Payment</InputLabel>
            <Select
              labelId="payment-status-filter-label"
              value={paymentStatusFilter}
              onChange={handlePaymentStatusFilterChange}
              label="Payment"
            >
              <MenuItem value="all">All Payments</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="free">Free</MenuItem>
            </Select>
          </FormControl>
          
          <Button 
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportToCsv}
          >
            Export
          </Button>
        </Box>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        ) : (
          <>
            {/* Attendees Table */}
            <TableContainer component={Paper}>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'name'}
                        direction={sortField === 'name' ? sortOrder : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'email'}
                        direction={sortField === 'email' ? sortOrder : 'asc'}
                        onClick={() => handleSort('email')}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'eventTitle' || sortField === 'tripTitle'}
                        direction={sortField === 'eventTitle' || sortField === 'tripTitle' ? sortOrder : 'asc'}
                        onClick={() => sortField === 'eventTitle' ? handleSort('tripTitle') : handleSort('eventTitle')}
                      >
                        Event/Trip
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'totalPrice'}
                        direction={sortField === 'totalPrice' ? sortOrder : 'asc'}
                        onClick={() => handleSort('totalPrice')}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortField === 'purchaseDate'}
                        direction={sortField === 'purchaseDate' ? sortOrder : 'asc'}
                        onClick={() => handleSort('purchaseDate')}
                      >
                        Purchase Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAttendees.length > 0 ? (
                    filteredAttendees
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((attendee) => (
                        <TableRow key={attendee.id} hover>
                          <TableCell>{attendee.name}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {attendee.email}
                              <IconButton 
                                size="small" 
                                onClick={() => navigator.clipboard.writeText(attendee.email)}
                                sx={{ ml: 1 }}
                              >
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              icon={attendee.source === 'event' ? <EventIcon fontSize="small" /> : <TripIcon fontSize="small" />}
                              label={attendee.source === 'event' ? 'Event' : 'Trip'} 
                              size="small"
                              color={attendee.source === 'event' ? 'primary' : 'secondary'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {attendee.source === 'event' ? attendee.eventTitle : attendee.tripTitle}
                          </TableCell>
                          <TableCell>{formatCurrency(attendee.totalPrice)}</TableCell>
                          <TableCell>{formatDate(attendee.purchaseDate)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={attendee.status} 
                              size="small" 
                              color={getStatusChipColor(attendee.status)}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={attendee.paymentStatus} 
                              size="small" 
                              color={getPaymentStatusChipColor(attendee.paymentStatus)}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => window.open(`mailto:${attendee.email}`, '_blank')}
                            >
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body1" sx={{ py: 3 }}>
                          No attendees found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredAttendees.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Box>
    </Container>
  );
};

export default OrganizationAttendees;